import React, { useState } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Bank } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CURRENCY_SYMBOLS = { INR: '\u20B9', USD: '$', EUR: '\u20AC', GBP: '\u00A3' };

export default function AddLoanDialog({ open, onClose, currency, onSuccess, loan }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const symbol = CURRENCY_SYMBOLS[currency] || currency;
  const isEdit = !!loan;

  const [amount, setAmount] = useState('');
  const [institution, setInstitution] = useState('');
  const [account, setAccount] = useState('');
  const [branch, setBranch] = useState('');
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (open && loan) {
      setAmount(String(loan.loan_amount || ''));
      setInstitution(loan.institution || '');
      setAccount(loan.account_number || '');
      setBranch(loan.branch_city || '');
    }
  }, [open, loan]);

  const reset = () => { setAmount(''); setInstitution(''); setAccount(''); setBranch(''); };

  const fee = amount && !isNaN(parseFloat(amount)) ? Math.round(parseFloat(amount) * 0.2) : 0;

  const handleSubmit = async () => {
    if (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) { toast.error('Valid loan amount is required'); return; }
    if (!institution.trim()) { toast.error('Bank / Financial Institution is required'); return; }
    if (!account.trim()) { toast.error('Loan account number is required'); return; }
    setSaving(true);
    try {
      const payload = {
        loan_amount: parseFloat(amount),
        institution: institution.trim(),
        account_number: account.trim(),
        branch_city: branch.trim(),
      };
      if (isEdit) {
        await axios.put(`${API}/loans/${loan.loan_id}`, payload, { withCredentials: true });
        toast.success('Loan updated');
      } else {
        await axios.post(`${API}/loans`, payload, { withCredentials: true });
        toast.success('Loan submitted for verification');
      }
      reset();
      onClose();
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save loan');
    } finally { setSaving(false); }
  };

  const inputCls = `h-10 rounded-sm font-ibm ${isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-50' : 'bg-zinc-50 border-zinc-200 text-zinc-950'}`;
  const labelCls = `font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] mb-1.5 block ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose(); } }}>
      <DialogContent className={`rounded-md max-w-md ${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`} data-testid="add-loan-dialog">
        <DialogHeader>
          <DialogTitle className={`font-outfit text-lg font-semibold flex items-center gap-2 ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>
            <Bank size={20} weight="bold" className="text-yellow-400" /> {isEdit ? 'Edit Loan' : 'Add Loan'}
          </DialogTitle>
          <DialogDescription className={`font-ibm text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
            Bank or NBFC loan. Goes to our team for verification, then a {symbol === '\u20B9' ? '20%' : '20%'} platform fee (Pay Now) enters it into the inter-cycle engine.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <label className={labelCls}>Loan Amount ({currency}) *</label>
            <Input value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" type="number" min="0" step="0.01" className={inputCls} data-testid="add-loan-amount" />
          </div>
          <div>
            <label className={labelCls}>Bank or Financial Institution *</label>
            <Input value={institution} onChange={e => setInstitution(e.target.value)} placeholder="e.g. HDFC Bank / Bajaj Finance" className={inputCls} data-testid="add-loan-institution" />
          </div>
          <div>
            <label className={labelCls}>Loan Account Number *</label>
            <Input value={account} onChange={e => setAccount(e.target.value)} placeholder="Loan account number" className={inputCls} data-testid="add-loan-account" />
          </div>
          <div>
            <label className={labelCls}>Branch or City</label>
            <Input value={branch} onChange={e => setBranch(e.target.value)} placeholder="Branch / City" className={inputCls} data-testid="add-loan-branch" />
          </div>

          {fee > 0 && (
            <div className={`p-3 rounded-sm border ${isDark ? 'border-yellow-400/30 bg-yellow-400/5' : 'border-yellow-200 bg-yellow-50'}`} data-testid="loan-fee-preview">
              <div className="flex items-center justify-between">
                <span className={`font-ibm text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>Platform fee (20%, Pay Now only)</span>
                <span className="font-outfit text-sm font-bold text-yellow-400">{symbol}{fee.toLocaleString()}</span>
              </div>
              <p className={`font-ibm text-[10px] mt-1 ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Fully refundable if no inter-cycle clears it within 90 days.</p>
            </div>
          )}
        </div>

        <div className="flex gap-3 mt-4">
          <button onClick={() => { reset(); onClose(); }} className={`flex-1 h-10 font-ibm text-sm font-medium rounded-sm border transition-all active:scale-[0.98] ${isDark ? 'border-zinc-700 text-zinc-400 hover:bg-zinc-800' : 'border-zinc-200 text-zinc-600 hover:bg-zinc-50'}`} data-testid="cancel-add-loan">Cancel</button>
          <button onClick={handleSubmit} disabled={saving} className="flex-1 h-10 bg-yellow-400 text-zinc-950 font-ibm text-sm font-semibold rounded-sm hover:bg-yellow-500 active:scale-[0.98] transition-all disabled:opacity-50" data-testid="submit-add-loan">
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Submit for Verification'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
